<script lang="ts">import { Accordion } from "@infinitro/accordion";
import { cfgBrandTitle, stateAffiliatesEnabled, cfgHelpcenterUrl, FeatureSet, stateFeatureSet, stateUi } from "@infinitro/config";
import { Container } from "@infinitro/container";
import { t, Verbiage } from "@infinitro/content";
import { handleIntercom, loggedIn, toSiteUrl } from "@infinitro/global";
import Link from "./link.svelte";
$: linkStyle = `
		display: block;
		font-size: 0.75rem;
		cursor: pointer;
		margin-bottom: 0.5rem;
		${useAlternative4 ? "margin-bottom: 0.75rem; font-weight: normal;" : "color: var(--color-text-500);"}
		text-decoration: none;
		&:hover { color: var(--color-text-100); }
	`;
$: internal = (path, label, extra = "") => `<a href="${toSiteUrl(path, $loggedIn)}" style="${linkStyle}" ${extra}>${label}</a>`;
const external = (href, label) => `<a href="${href}" target="_blank" rel="noopener noreferrer" style="${linkStyle}">${label}</a>`;
const toAccordionHtml = (link) => {
	if (link.external) return external(link.href, link.label);
	const extra = link.reload ? "data-sveltekit-reload" : "";
	return `<a href="${link.href}" style="${linkStyle}" ${extra}>${link.label}</a>`;
};
const helpcenterIsAbsolute = (u) => u.startsWith("http://") || u.startsWith("https://");
$: hcUrl = cfgHelpcenterUrl();
$: footerLinksCasinoSportsbookLayout = $stateUi.footer?.footerLinksLayout === "with_casino_sportsbook";
$: showAffiliatesLink = $stateAffiliatesEnabled;
$: accordionSections = [
	{
		title: $t(Verbiage.support),
		links: [
			helpcenterIsAbsolute(hcUrl) ? {
				href: hcUrl,
				label: $t(Verbiage.helpdesk)
			} : {
				href: toSiteUrl("/helpcenter", $loggedIn),
				label: $t(Verbiage.helpdesk),
				reload: true
			},
			{
				href: toSiteUrl("/responsible-gaming/", $loggedIn),
				label: $t(Verbiage.responsible_gaming)
			},
			{
				href: "https://www.gamcare.org.uk/",
				label: $t(Verbiage.gamble_aware),
				external: true
			}
		]
	},
	{
		title: $t(Verbiage.platform),
		links: [
			{
				href: toSiteUrl("/about-us/", $loggedIn),
				label: $t(Verbiage.about_us)
			},
			{
				href: toSiteUrl("/fairness/", $loggedIn),
				label: $t(Verbiage.provably_fair)
			},
			...showAffiliatesLink ? [{
				href: toSiteUrl("/affiliate/", $loggedIn),
				label: $t(Verbiage.affiliates)
			}] : []
		]
	},
	{
		title: $t(Verbiage.terms),
		links: [
			{
				href: toSiteUrl("/terms-of-service/", $loggedIn),
				label: $t(Verbiage.terms_of_service)
			},
			{
				href: toSiteUrl("/bonus-terms/", $loggedIn),
				label: $t(Verbiage.bonus_terms)
			},
			{
				href: toSiteUrl("/terms-sports/", $loggedIn),
				label: $t(Verbiage.sports_terms)
			},
			{
				href: toSiteUrl("/complaints-and-arbitration/", $loggedIn),
				label: $t(Verbiage.complaints_and_arbitration)
			}
		]
	},
	{
		title: $t(Verbiage.privacy),
		links: [
			{
				href: toSiteUrl("/payment-policy/", $loggedIn),
				label: $t(Verbiage.payment_policy)
			},
			{
				href: toSiteUrl("/aml-policy/", $loggedIn),
				label: $t(Verbiage.aml_policy)
			},
			{
				href: toSiteUrl("/privacy-policy/", $loggedIn),
				label: $t(Verbiage.privacy_policy)
			},
			...$stateFeatureSet === FeatureSet.Preview ? [{
				href: toSiteUrl("/kyc-policy/", $loggedIn),
				label: $t(Verbiage.kyc_policy)
			}] : []
		]
	}
];
$: useAlternative4 = $stateUi.footer?.footerStyle === "alternative4";
</script>

{#if footerLinksCasinoSportsbookLayout}
	<Container>
		<div class="accordion-section">
			<div class="accordion-grid">
				<Accordion title={cfgBrandTitle()} text="" isHtml={false} additionalContent="" open={false}>
					<div slot="content" class="footer-accordion-link-stack">
						<Link path="/vip/" verbiage={Verbiage.vip_galaxy} />
						<Link path="/about-us/" verbiage={Verbiage.about_us} />
						<Link path="/promotion-page/welcome-deposit-bonus/" verbiage={Verbiage.welcome_bonus} />
						{#if showAffiliatesLink}
							<Link path="/affiliate/" verbiage={Verbiage.affiliates} />
						{/if}
						<button
							type="button"
							class="footer-link footer-link-plain-button"
							on:click|preventDefault={handleIntercom}
						>
							<span>{$t(Verbiage.help_center)}</span>
						</button>
					</div>
				</Accordion>

				<Accordion
					title={$t(Verbiage.casino)}
					text=""
					isHtml={false}
					additionalContent=""
					open={false}
				>
					<div slot="content" class="footer-accordion-link-stack">
						<Link path="/originals/" verbiage={Verbiage.originals} />
						<Link path="/slots/" verbiage={Verbiage.slots} />
						<Link path="/livecasino/" verbiage={Verbiage.live_casino} />
						<Link path="/gameshows/" verbiage={Verbiage.game_shows} />
					</div>
				</Accordion>

				<Accordion
					title={$t(Verbiage.sportsbook)}
					text=""
					isHtml={false}
					additionalContent=""
					open={false}
				>
					<div slot="content" class="footer-accordion-link-stack">
						<Link path="/sports/live/" verbiage={Verbiage.live_bets} />
						<Link path="/sports/soccer-1" verbiage={Verbiage.football} />
						<Link path="/sports/tennis-5" verbiage={Verbiage.tennis} />
						<Link path="/sports/basketball-2" verbiage={Verbiage.basketball} />
					</div>
				</Accordion>

				<Accordion
					title={$t(Verbiage.terms)}
					text=""
					isHtml={false}
					additionalContent=""
					open={false}
				>
					<div slot="content" class="footer-accordion-link-stack">
						<Link path="/terms-of-service/" verbiage={Verbiage.terms_of_service} />
						<Link path="/bonus-terms/" verbiage={Verbiage.bonus_terms} />
						<Link path="/terms-sports/" verbiage={Verbiage.sports_terms} />
					</div>
				</Accordion>

				<Accordion
					title={$t(Verbiage.privacy)}
					text=""
					isHtml={false}
					additionalContent=""
					open={false}
				>
					<div slot="content" class="footer-accordion-link-stack">
						<Link path="/payment-policy/" verbiage={Verbiage.payment_policy} />
						<Link path="/aml-policy/" verbiage={Verbiage.aml_policy} />
						<Link path="/privacy-policy/" verbiage={Verbiage.privacy_policy} />
						<Link path="/kyc-policy/" verbiage={Verbiage.kyc_policy} />
					</div>
				</Accordion>
			</div>
		</div>
	</Container>
{:else}
	<Container>
		<div class="accordion-section">
			<div class="accordion-grid">
				{#each accordionSections as section}
					<Accordion {...section} />
				{/each}
			</div>
		</div>
	</Container>
{/if}

<style>
	.accordion-section {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		gap: 1rem
}

	.accordion-grid {
		display: grid;
		width: 100%;
		grid-template-columns: repeat(1, minmax(0, 1fr));
		gap: 1rem
}

	.footer-accordion-link-stack {
		display: flex;
		flex-direction: column;
		align-items: flex-start
}

	/* Match `Link` / `.footer-link` — button is outside Link so it does not get scoped link styles. */
	:global(.footer-accordion-link-stack .footer-link-plain-button) {
		width: 100%;
		max-width: 100%;
		cursor: pointer;
		-webkit-appearance: none;
		   -moz-appearance: none;
		        appearance: none;
		border-width: 0px;
		background-color: transparent;
		padding: 0px;
		text-align: left;
		/* Match scoped `Link` margins: `.footer-link` uses mb-1.5 + mt-1.5 on odd/even children */
		margin-top: 0.375rem;
		margin-bottom: 0.375rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 400;
		line-height: 16.8px;
		color: var(--color-text-300);
		font-family: var(--font-main)
}
	:global(.footer-accordion-link-stack .footer-link-plain-button:hover) {
		color: var(--color-text-100)
}
	@media (min-width: 768px) {
		:global(.footer-accordion-link-stack .footer-link-plain-button) {
				font-size: 0.875rem;
				line-height: 1.25rem
		}
	}
</style>
